import React, { useState } from 'react';
import { PlusCircle } from 'lucide-react';
import { Subscription, SubscriptionService, Service } from '@/lib/data';
import { Button } from '@/components/ui/button';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useSubscriptionServices } from '@/hooks/useSubscriptionServices';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { SelectedServiceState, ServiceFormValues } from '@/types/serviceTypes';

import ServicesTable from './ServicesTable';
import EmptyServicesState from './EmptyServicesState';
import ServiceManagementDialog from './ServiceManagementDialog';

interface SubscriptionServicesProps {
  subscription: Subscription;
  onRefresh: () => void;
}

const serviceSchema = z.object({
  service_id: z.string(),
  status: z.enum(['registered', 'active', 'cancelled', 'terminated']),
  registration_date: z.date(),
  activation_date: z.date().nullable(),
  termination_date: z.date().nullable(),
  cancellation_date: z.date().nullable(),
  price: z.coerce.number().nullable(),
});

const SubscriptionServices: React.FC<SubscriptionServicesProps> = ({ subscription, onRefresh }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [editingService, setEditingService] = useState<SubscriptionService | null>(null);
  const [isAddingService, setIsAddingService] = useState(false);
  const [selectedServices, setSelectedServices] = useState<Record<string, SelectedServiceState>>({});
  const { updateService, addService, removeService, isLoading, allServices } = useSubscriptionServices(subscription.subscription_id);
  const { user } = useAuth();

  React.useEffect(() => {
    console.log("Selected services in selection list:", 
      Object.entries(selectedServices)
        .filter(([_, state]) => state && state.selected)
        .map(([id]) => id)
    );
  }, [selectedServices]);

  const groupedServices = subscription.services.reduce((groups, service) => {
    const parentId = service.service.parent_service_id;
    if (!parentId) {
      if (!groups[service.service_id]) {
        groups[service.service_id] = {
          service,
          children: []
        };
      } else {
        groups[service.service_id].service = service;
      }
    } else {
      if (!groups[parentId]) {
        groups[parentId] = {
          service: null,
          children: [service]
        };
      } else {
        groups[parentId].children.push(service);
      }
    }
    return groups;
  }, {} as Record<string, { service: SubscriptionService | null; children: SubscriptionService[] }>);

  const getAvailableServices = () => {
    console.log("=== DEBUG: getAvailableServices ===");
    console.log("All services from API:", allServices.map(s => ({ id: s.service_id, name: s.service_name, parent: s.parent_service_id })));
    console.log("Subscription services:", subscription.services.map(s => ({ id: s.service_id, name: s.service.service_name })));
    
    // Get the exact service_ids that are already in the subscription
    const existingServiceIds = subscription.services.map(s => s.service_id);
    console.log("Existing service IDs in subscription:", existingServiceIds);
    
    // Filter out only services whose exact service_id is already in the subscription
    const availableServices = allServices.filter(service => {
      const isAlreadyAdded = existingServiceIds.includes(service.service_id);
      console.log(`Service ${service.service_name} (${service.service_id}): already added = ${isAlreadyAdded}`);
      return !isAlreadyAdded;
    });
    
    console.log("Available services:", availableServices.map(s => ({ id: s.service_id, name: s.service_name, parent: s.parent_service_id })));
    console.log("=== END DEBUG ===");
    
    return availableServices;
  };

  const groupServicesByParent = (services: Service[]) => {
    const parentServices = services.filter(service => !service.parent_service_id);
    const childServices = services.filter(service => service.parent_service_id);
    
    return parentServices.map(parentService => ({
      parent: parentService,
      children: childServices.filter(child => child.parent_service_id === parentService.service_id)
    }));
  };

  const handleEditService = (service: SubscriptionService) => {
    console.log("Editing service:", service);
    // Double-check ID exists and log it for debugging
    console.log("Service ID to be edited:", service.id);
    if (!service.id) {
      toast.error("Cannot edit service: Missing ID");
      return;
    }
    setEditingService(service);
    setIsAddingService(false);
    setIsOpen(true);
  };

  const handleAddService = () => {
    console.log("Add service button clicked");
    setEditingService(null);
    setIsAddingService(true);
    
    const availableServices = getAvailableServices();
    const servicesState: Record<string, SelectedServiceState> = {};
    availableServices.forEach(service => {
      servicesState[service.service_id] = { 
        selected: false,
        price: service.default_price 
      };
    });
    
    setSelectedServices(servicesState);
    setIsOpen(true);
  };

  const toggleServiceSelection = (serviceId: string) => {
    if (!isOpen) return;
    
    console.log("Toggling service selection:", serviceId);
    
    setSelectedServices(prev => {
      const newState = { ...prev };
      
      if (!newState[serviceId]) {
        const service = allServices.find(s => s.service_id === serviceId);
        if (service) {
          newState[serviceId] = {
            selected: true,
            price: service.default_price
          };
        }
        return newState;
      }
      
      newState[serviceId] = {
        ...newState[serviceId],
        selected: !newState[serviceId].selected
      };
      
      console.log("New selected services state:", 
        Object.entries(newState)
          .filter(([_, state]) => state && state.selected)
          .map(([id]) => id)
      );
      
      return newState;
    });
  };

  const updateServicePrice = (serviceId: string, price: number | null) => {
    setSelectedServices(prev => {
      const newState = { ...prev };
      
      if (!newState[serviceId]) {
        const service = allServices.find(s => s.service_id === serviceId);
        if (service) {
          newState[serviceId] = {
            selected: false,
            price: price ?? service.default_price
          };
        }
        return newState;
      }
      
      newState[serviceId] = {
        ...newState[serviceId],
        price: price
      };
      
      return newState;
    });
  };

  const onSubmit = async (data: ServiceFormValues) => {
    console.log("onSubmit called with data:", data);
    
    if (!user) {
      toast.error("You must be logged in to modify services");
      return;
    }

    try {
      if (editingService) {
        console.log("Updating existing service with ID:", editingService.id);
        
        // Ensure ID is correctly passed for update
        await updateService({
          ...data,
          id: editingService.id, // Ensure ID is first and prominent
          subscription_id: subscription.subscription_id,
          service_id: editingService.service_id
        });
        
        toast.success("Service updated successfully");
        setIsOpen(false);
        onRefresh();
      } else if (isAddingService) {
        console.log("Adding new services with form data:", data);
        console.log("Selected services:", selectedServices);
        
        const servicesToAdd = Object.entries(selectedServices)
          .filter(([_, { selected }]) => selected)
          .map(([serviceId, { price }]) => {
            const service = allServices.find(s => s.service_id === serviceId);
            return {
              subscription_id: subscription.subscription_id,
              service_id: serviceId,
              status: data.status,
              registration_date: data.registration_date,
              activation_date: data.activation_date,
              cancellation_date: data.cancellation_date,
              termination_date: data.termination_date,
              price: price
            };
          });

        console.log("Services to add:", servicesToAdd);

        if (servicesToAdd.length === 0) {
          toast.error("Please select at least one service to add");
          return;
        }

        for (const serviceData of servicesToAdd) {
          console.log("Adding individual service:", serviceData);
          try {
            await addService(serviceData);
            console.log("Service added successfully:", serviceData.service_id);
          } catch (error) {
            console.error("Error adding specific service:", serviceData.service_id, error);
            throw error;
          }
        }

        toast.success(`${servicesToAdd.length} service(s) added successfully`);
        setIsOpen(false);
        onRefresh();
      }
    } catch (error) {
      console.error("Error saving service:", error);
      
      let errorMessage = "An unknown error occurred";
      if (error instanceof Error) {
        errorMessage = error.message;
      }
      
      if (errorMessage.includes('violates row-level security policy')) {
        toast.error('Permission denied: You do not have permission to modify this service');
      } else {
        toast.error(editingService ? 
          `Failed to update service: ${errorMessage}` : 
          `Failed to add service: ${errorMessage}`
        );
      }
    }
  };

  const handleDeleteService = async (serviceId: string) => {
    if (!user) {
      toast.error("You must be logged in to remove services");
      return;
    }

    if (!window.confirm("Are you sure you want to remove this service?")) {
      return;
    }

    try {
      await removeService(serviceId, user.email);
      toast.success("Service removed successfully");
      onRefresh();
    } catch (error) {
      console.error("Error removing service:", error);
      
      if (error instanceof Error && error.message.includes('violates row-level security policy')) {
        toast.error('Permission denied: You do not have permission to remove this service');
      } else {
        toast.error("Failed to remove service");
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium">Services</h3>
        <Button onClick={handleAddService} disabled={isLoading} size="sm">
          <PlusCircle className="h-4 w-4 mr-2" />
          Add
        </Button>
      </div>

      {subscription.services.length === 0 ? (
        <EmptyServicesState handleAddService={handleAddService} />
      ) : (
        <ServicesTable 
          groupedServices={groupedServices}
          handleEditService={handleEditService}
          handleDeleteService={handleDeleteService}
          isLoading={isLoading}
        />
      )}

      <ServiceManagementDialog
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        editingService={editingService}
        isAddingService={isAddingService}
        subscription={subscription}
        selectedServices={selectedServices}
        setSelectedServices={setSelectedServices}
        availableServices={allServices}
        isLoading={isLoading}
        onSubmit={onSubmit}
        groupServicesByParent={groupServicesByParent}
        getAvailableServices={getAvailableServices}
        toggleServiceSelection={toggleServiceSelection}
        updateServicePrice={updateServicePrice}
      />
    </div>
  );
};

export default SubscriptionServices;
